import { HttpHeaders } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal, DestroyRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { finalize, take } from 'rxjs';
import { AbsenceRequest } from 'src/app/models/absence_request.model';
import { AbsenceRequestUpsertPayload } from 'src/app/models/absence_request.model';
import { Document } from 'src/app/models/document.model';
import { EmployeeCalendarRegister } from 'src/app/models/employee_calendar_register.model';
import { Holiday } from 'src/app/models/holiday.model';
import { User } from 'src/app/models/user.model';
import { WorkingTimeCategory } from 'src/app/models/working_time_category.model';
import { ApiService, PermissionsService, UserService } from 'src/app/services';
import { DaysAllowedTypeEnum } from 'src/app/enums/days-allowed-type.enum';
import { ComponentCanDeactivate, PendingChangesGuard } from 'src/app/guards/pending-changes.guard';
import { AbsenceCategoriesLegendComponent } from '../absence-categories-legend/absence-categories-legend.component';
import { ClockRegisterService } from 'src/app/services/clock-register.service';
// Habilitar plugins de dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

export interface HolidaysActualUser {
  holidays: Holiday[];
  employee_calendar_registers: EmployeeCalendarRegister[];
}

interface AbsenceRequestFormPayload {
  start_date: Date | string | null;
  end_date: Date | string | null;
  start_time: string;
  end_time: string;
  working_time_category_id: number | string | null;
  justification: string | null;
  user_id?: number | null;
}

interface AbsenceRequestDateNormalizedPayload {
  start_date: string;
  end_date: string;
  working_time_category_id: number;
  justification: string | null;
}

type AbsenceRequestWithDocument = Omit<AbsenceRequest, 'document'> & { document: Document | null };

@Component({
  selector: 'app-my-absence-requests-detail',
  templateUrl: './my-absence-requests-detail.component.html',
  styleUrl: './my-absence-requests-detail.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatSelectModule,
    MatInputModule,
    MatTooltipModule,
    MatCheckboxModule,
    RouterModule,
    AbsenceCategoriesLegendComponent,
  ],
  standalone: true,
})
export class MyAbsenceRequestsDetailComponent implements ComponentCanDeactivate {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private api = inject(ApiService);
  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroy_ref = inject(DestroyRef);
  private clock = inject(ClockRegisterService);
  private readonly permissions = inject(PermissionsService);
  private readonly userService = inject(UserService);
  private readonly pending_changes_guard = inject(PendingChangesGuard);

  protected selectedCategory = signal<WorkingTimeCategory | null>(null);
  protected dateAdjustmentMessage = signal<string | null>(null);
  protected absencesWorkingTimeCategories = signal<WorkingTimeCategory[]>([]);
  protected holidaysActualUser = signal<HolidaysActualUser | null>(null);
  protected isClockedIn = toSignal(this.clock.statusObservable, { initialValue: false });
  protected readonly workers = signal<User[]>([]);

  /** Gestionar (RRHH absences-management) + direction/rrhh. */
  protected readonly isManageForOthers =
    this.permissions.validateDepartment(['direction', 'rrhh']) && this.router.url.includes('/rrhh/absences-management');

  protected isAllDay = signal<boolean>(true);

  protected readonly hasUnlimitedDaysAllowed = computed<boolean>(() => {
    const days_allowed = this.selectedCategory()?.active_legal_workers_agreement_working_time_category?.days_allowed;
    return days_allowed === null;
  });

  protected minDate = signal<Date>(
    this.isManageForOthers ? dayjs('2000-01-01').startOf('day').toDate() : dayjs().subtract(1, 'month').startOf('day').toDate()
  );

  private holidays: string[] = [];
  private is_adjusting_date = false;

  protected form: FormGroup = this.fb.group({
    user_id: [this.userService.getCurrentUser().id, Validators.required],
    working_time_category_id: ['', Validators.required],
    start_date: [null, Validators.required],
    end_date: [null, Validators.required],
    start_time: [''],
    end_time: [''],
    justification: ['', Validators.maxLength(1000)],
  });

  /** Absence request pasado desde la lista al pulsar "Solicitar modificación" (router STATE). */
  private readonly absenceRequestFromState = signal<AbsenceRequestWithDocument | null>(null);

  protected readonly isModificationMode = computed((): boolean => {
    const id = this.route.snapshot.paramMap.get('id');
    return id != null && id !== 'create' && /^\d+$/.test(id);
  });

  protected readonly originalHasDocument = computed((): boolean => {
    return this.isModificationMode() && this.absenceRequestFromState()?.document !== null;
  });

  protected keepDocument = signal<boolean>(true);

  protected readonly backLink = computed((): string[] => (this.isModificationMode() ? ['../..'] : ['..']));

  protected readonly canSave = signal<boolean>(false);
  protected readonly saving = signal(false);

  protected readonly canSubmitWhileClockedIn = computed((): boolean => this.isManageForOthers || !this.isClockedIn());

  /** Once the original absence has started, workers cannot change the start date (end remains editable). */
  protected readonly hasAbsenceStarted = computed((): boolean => {
    const ar = this.absenceRequestFromState();
    if (ar?.start_date == null) {
      return false;
    }
    return dayjs(ar.start_date).isSameOrBefore(dayjs());
  });

  protected readonly isStartDateLocked = computed(
    (): boolean => this.isModificationMode() && this.hasAbsenceStarted() && !this.isManageForOthers
  );

  protected readonly is_consecutive_category = computed(
    (): boolean => this.selectedCategory()?.active_legal_workers_agreement_working_time_category?.is_days_to_enjoy_consecutive === true
  );

  private initialFormValueSnapshot: string | null = null;
  private hasPatchedFormForModification = false;

  constructor() {
    this.pending_changes_guard.registerBeforeUnload(this, this.destroy_ref);

    const navigation_state = this.router.getCurrentNavigation()?.extras?.state as { absenceRequest?: AbsenceRequest } | undefined;
    const history_state = history.state as { absenceRequest?: AbsenceRequest } | undefined;
    const request_from_navigation = navigation_state?.absenceRequest ?? history_state?.absenceRequest;
    const request_from_state: AbsenceRequestWithDocument | null =
      request_from_navigation != null ? { ...request_from_navigation, document: request_from_navigation.document ?? null } : null;
    this.absenceRequestFromState.set(request_from_state);

    if (this.isModificationMode() && request_from_state == null) {
      this.router.navigate(this.backLink(), { relativeTo: this.route });
      return;
    }

    // Empleados: no pueden modificar ausencias de hace más de 1 mes. RRHH/admin sí.
    if (
      !this.isManageForOthers &&
      this.isModificationMode() &&
      request_from_state?.start_date != null &&
      dayjs(request_from_state.start_date).isBefore(dayjs().subtract(1, 'month'), 'day')
    ) {
      this.router.navigate(this.backLink(), { relativeTo: this.route });
      return;
    }

    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => this.recomputeCanSave());
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(() => this.recomputeCanSave());

    this.form
      .get('start_date')
      ?.valueChanges?.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => this.onStartDateChange());

    if (this.isManageForOthers) {
      this.loadWorkers();
    }

    if (this.isModificationMode() && request_from_state?.user_id != null) {
      this.form.patchValue({ user_id: request_from_state.user_id });
      this.form.get('user_id')?.disable();
    }

    this.loadWorkingTimeCategories();
    this.loadHolidaysAndUserData();
    this.recomputeCanSave();
  }

  private onSuccessAndNavigate(): void {
    this.initialFormValueSnapshot = null;
    this.form.markAsPristine();
    this.recomputeCanSave();
    this.router.navigate(this.backLink(), { relativeTo: this.route });
  }

  private recomputeCanSave(): void {
    if (!this.form.valid) {
      this.canSave.set(false);
      return;
    }

    if (!this.isModificationMode()) {
      this.canSave.set(true);
      return;
    }

    this.canSave.set(this.hasFormChanged());
  }

  canDeactivate(): boolean {
    return !this.hasFormChanged();
  }

  protected submit(): void {
    if (this.saving()) return;

    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }

    const formPayload: AbsenceRequestFormPayload = this.form.getRawValue() as AbsenceRequestFormPayload;
    const normalizedPayload = this.manageEndDate(formPayload);
    const payload = this.convertDatesToUTC(normalizedPayload);

    const headers = new HttpHeaders().set('X-Snackbar-Duration', '10000');
    const upsertPayload: AbsenceRequestUpsertPayload = {
      working_time_category_id: payload.working_time_category_id,
      start_date: payload.start_date,
      end_date: payload.end_date,
      justification: payload.justification,
      keep_document: this.originalHasDocument() ? this.keepDocument() : true,
    };

    if (this.isManageForOthers && !this.isModificationMode() && formPayload.user_id != null) {
      upsertPayload.user_id = Number(formPayload.user_id);
    }

    this.saving.set(true);

    if (this.isModificationMode()) {
      const id = this.route.snapshot.paramMap.get('id');
      if (id == null || !/^\d+$/.test(id)) {
        this.saving.set(false);
        return;
      }
      this.api
        .modifyAbsenceRequest(+id, upsertPayload, { headers: headers })
        .pipe(
          takeUntilDestroyed(this.destroy_ref),
          finalize(() => this.saving.set(false))
        )
        .subscribe({
          next: () => this.onSuccessAndNavigate(),
        });
    } else {
      this.api
        .createAbsenceRequest(upsertPayload, { headers: headers })
        .pipe(
          takeUntilDestroyed(this.destroy_ref),
          finalize(() => this.saving.set(false))
        )
        .subscribe({
          next: () => this.onSuccessAndNavigate(),
        });
    }
  }

  private loadWorkers(): void {
    this.api
      .getUsers({
        scopes: 'activeWorkers',
        order_by: 'name:asc',
        per_page: 500,
      })
      .pipe(take(1), takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: response => this.workers.set(response.data ?? []),
      });
  }

  protected onWorkingTimeCategoryChange(event: MatSelectChange): void {
    const category = this.absencesWorkingTimeCategories().find((cat: WorkingTimeCategory) => cat.id === event.value);

    if (category) {
      this.selectedCategory.set(category);

      const is_consecutive = category?.active_legal_workers_agreement_working_time_category?.is_days_to_enjoy_consecutive;

      this.isAllDay.set(category.active_legal_workers_agreement_working_time_category!.all_day);

      if (is_consecutive) {
        this.form.get('end_date')?.disable();
        this.form.get('end_date')?.setValidators([Validators.required]);
        this.form.get('start_time')?.clearValidators();
        this.form.get('end_time')?.clearValidators();
        this.form.get('start_time')?.setValue('');
        this.form.get('end_time')?.setValue('');
        this.form.get('start_time')?.updateValueAndValidity();
        this.form.get('end_time')?.updateValueAndValidity();
        if (this.form.get('start_date')?.value) {
          this.onStartDateChange();
        }
      } else {
        this.form.get('end_date')?.enable();

        const currentEndDate = this.form.get('end_date')?.value;
        if (!(this.isModificationMode() && currentEndDate)) {
          this.form.get('end_date')?.setValue(null);
        }

        if (this.isAllDay()) {
          this.form.get('end_date')?.setValidators([Validators.required]);
          this.form.get('start_time')?.clearValidators();
          this.form.get('end_time')?.clearValidators();
        } else {
          this.form.get('start_time')?.setValidators([Validators.required]);
          this.form.get('end_time')?.setValidators([Validators.required]);
          this.form.get('end_date')?.clearValidators();
        }
        this.form.get('end_date')?.updateValueAndValidity();
        this.form.get('start_time')?.updateValueAndValidity();
        this.form.get('end_time')?.updateValueAndValidity();
        this.dateAdjustmentMessage.set(null);
      }

      this.applyStartedAbsenceModificationLocks();
      this.recomputeCanSave();
    }
  }

  protected onStartDateChange(): void {
    if (this.is_adjusting_date) {
      return;
    }

    const start_date_value = this.form.get('start_date')?.value;
    const category = this.selectedCategory();

    if (!start_date_value) {
      this.dateAdjustmentMessage.set(null);
      this.form.get('end_date')?.setValue(null);
      return;
    }

    const is_consecutive = category?.active_legal_workers_agreement_working_time_category?.is_days_to_enjoy_consecutive;

    if (is_consecutive && category) {
      const event_date = new Date(start_date_value);
      const result = this.calculateConsecutiveDates(event_date, category);

      if (result.wasAdjusted) {
        this.is_adjusting_date = true;
        const adjustedDateString = dayjs(result.adjustedStartDate).format('YYYY-MM-DD');
        this.form.get('start_date')?.setValue(adjustedDateString, { emitEvent: false });
        this.is_adjusting_date = false;
        this.dateAdjustmentMessage.set(result.message);
      } else {
        this.dateAdjustmentMessage.set(null);
      }

      const endDateString = dayjs(result.end_date).format('YYYY-MM-DD');
      this.form.get('end_date')?.setValue(endDateString);
    } else {
      this.form.get('end_date')?.setValue(null);

      this.dateAdjustmentMessage.set(null);
    }
  }

  /**
   * Calcula las fechas de inicio y fin de un permiso consecutivo según la normativa del Tribunal Supremo.
   *
   * STS 13/02/2018 (n.º 266/2016): El cómputo de los permisos retribuidos debe iniciarse
   * el primer día laborable inmediatamente posterior al hecho causante, salvo que el
   * convenio colectivo especifique lo contrario (ej: matrimonio en días naturales).
   *
   * STS 257/2020, 17/03 (rec. 193/2018): Se precisa que si el día de la ceremonia es laborable deberá computarse dentro de los quince días, * puesto que en caso contrario supondría en realidad el reconocimiento de dieciséis días de permiso; pero, si la celebración del matrimonio se lleva a cabo en un día festivo o no laborable para la persona trabajadora, el día inicial del permiso por matrimonio será el siguiente laborable a dicha celebración.
   */
  private calculateConsecutiveDates(
    event_date: Date,
    category: WorkingTimeCategory
  ): { adjustedStartDate: Date; end_date: Date; wasAdjusted: boolean; message: string } {
    const days_allowed = category.active_legal_workers_agreement_working_time_category?.days_allowed || 0;
    const days_allowed_type =
      category.active_legal_workers_agreement_working_time_category?.days_allowed_type || DaysAllowedTypeEnum.CALENDAR_DAYS;

    let startDate = new Date(event_date);
    let wasAdjusted = false;
    let message = '';

    if (days_allowed_type === DaysAllowedTypeEnum.WORKING_DAYS) {
      if (this.isWeekend(startDate) || this.isHoliday(startDate)) {
        const original_date = new Date(startDate);
        startDate = this.getNextWorkingDay(startDate);
        wasAdjusted = true;
        message = this.buildAdjustmentMessage(original_date, startDate, 'laborable');
      }
      const end_date = this.addWorkingDays(startDate, days_allowed);
      return { adjustedStartDate: startDate, end_date, wasAdjusted, message };
    }

    if (days_allowed_type === DaysAllowedTypeEnum.BUSINESS_DAYS) {
      if (this.isHoliday(startDate)) {
        const original_date = new Date(startDate);
        startDate = this.getNextBusinessDay(startDate);
        wasAdjusted = true;
        message = `La fecha seleccionada (${this.formatDate(original_date)}) es festivo. El permiso comenzará el primer día hábil: ${this.formatDate(startDate)}.`;
      }
      const end_date = this.addBusinessDays(startDate, days_allowed);
      return { adjustedStartDate: startDate, end_date, wasAdjusted, message };
    }

    const end_date = this.addDays(startDate, days_allowed - 1);
    return { adjustedStartDate: startDate, end_date, wasAdjusted, message };
  }

  private buildAdjustmentMessage(original_date: Date, newDate: Date, dayType: string): string {
    const dayName = this.getDayName(original_date);
    const isWeekend = this.isWeekend(original_date);
    const isHoliday = this.isHoliday(original_date);

    if (isWeekend && isHoliday) {
      return `La fecha seleccionada (${dayName}, ${this.formatDate(original_date)}) es fin de semana y festivo. El permiso debe comenzar el primer día ${dayType}: ${this.formatDate(newDate)}.`;
    }
    if (isWeekend) {
      return `La fecha seleccionada (${dayName}, ${this.formatDate(original_date)}) es fin de semana. El permiso debe comenzar el primer día ${dayType}: ${this.formatDate(newDate)}.`;
    }
    return `La fecha seleccionada (${this.formatDate(original_date)}) es festivo. El permiso debe comenzar el primer día ${dayType}: ${this.formatDate(newDate)}.`;
  }

  private isHoliday(date: Date): boolean {
    return this.holidays.includes(dayjs(date).format('YYYY-MM-DD'));
  }

  private isWeekend(date: Date): boolean {
    const day = date.getDay();
    return day === 0 || day === 6;
  }

  private addDays(date: Date, days: number): Date {
    const new_date = new Date(date);
    new_date.setDate(new_date.getDate() + days);
    return new_date;
  }

  private getNextWorkingDay(date: Date): Date {
    let nextDay = this.addDays(date, 1);
    while (this.isWeekend(nextDay) || this.isHoliday(nextDay)) {
      nextDay = this.addDays(nextDay, 1);
    }
    return nextDay;
  }

  private getNextBusinessDay(date: Date): Date {
    let nextDay = this.addDays(date, 1);
    while (this.isHoliday(nextDay)) {
      nextDay = this.addDays(nextDay, 1);
    }
    return nextDay;
  }

  private addWorkingDays(startDate: Date, workingDays: number): Date {
    let currentDate = new Date(startDate);
    let counted = !this.isWeekend(currentDate) && !this.isHoliday(currentDate) ? 1 : 0;

    while (counted < workingDays) {
      currentDate = this.addDays(currentDate, 1);
      if (!this.isWeekend(currentDate) && !this.isHoliday(currentDate)) {
        counted++;
      }
    }
    return currentDate;
  }

  private addBusinessDays(startDate: Date, businessDays: number): Date {
    let currentDate = new Date(startDate);
    let counted = !this.isHoliday(currentDate) ? 1 : 0;

    while (counted < businessDays) {
      currentDate = this.addDays(currentDate, 1);
      if (!this.isHoliday(currentDate)) {
        counted++;
      }
    }
    return currentDate;
  }

  private formatDate(date: Date): string {
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  private getDayName(date: Date): string {
    const days = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
    return days[date.getDay()];
  }

  private manageEndDate(payload: AbsenceRequestFormPayload): AbsenceRequestDateNormalizedPayload {
    const working_time_category_id = Number(payload.working_time_category_id);
    const justification = payload.justification ?? null;

    if (!this.isAllDay()) {
      const date_str = dayjs(payload.start_date).format('YYYY-MM-DD');
      const start_time = payload.start_time ?? '';
      const end_time = payload.end_time ?? '';
      return {
        working_time_category_id,
        justification,
        start_date: dayjs(`${date_str} ${start_time}`).format('YYYY-MM-DD HH:mm:ss'),
        end_date: dayjs(`${date_str} ${end_time}`).format('YYYY-MM-DD HH:mm:ss'),
      };
    }

    return {
      working_time_category_id,
      justification,
      start_date: dayjs(payload.start_date).format('YYYY-MM-DD HH:mm:ss'),
      end_date: dayjs(payload.end_date).format('YYYY-MM-DD HH:mm:ss'),
    };
  }

  /**
   * Convierte las fechas start_date y end_date del payload a UTC
   * usando dayjs.tz para interpretar la fecha en la zona horaria del evento,
   * no en la zona horaria actual (importante para DST)
   */
  private convertDatesToUTC(payload: AbsenceRequestDateNormalizedPayload): AbsenceRequestDateNormalizedPayload {
    const user_time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start_date = dayjs.tz(payload.start_date, user_time_zone).utc().format('YYYY-MM-DD HH:mm:ss');
    const end_date = dayjs.tz(payload.end_date, user_time_zone).utc().format('YYYY-MM-DD HH:mm:ss');

    return {
      ...payload,
      start_date,
      end_date,
    };
  }

  private loadWorkingTimeCategories(): void {
    this.api
      .getAllAbsenceRequestWorkingTimeCategories({
        is_working_time: false,
        with: 'active_legal_workers_agreement_working_time_category',
      })
      .pipe(take(1), takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: categories => {
          this.absencesWorkingTimeCategories.set(categories);
          if (this.isModificationMode() && this.absenceRequestFromState() != null && !this.hasPatchedFormForModification) {
            this.hasPatchedFormForModification = true;
            this.patchFormFromAbsenceRequest(this.absenceRequestFromState()!);
            this.setInitialFormValueSnapshot();
          }
        },
      });
  }

  private loadHolidaysAndUserData(): void {
    const yesterday = new Date(new Date().setDate(new Date().getDate() - 1));
    const next_day = new Date(new Date().setFullYear(new Date().getFullYear() + 1));

    this.api
      .getMyAbsences(yesterday, next_day)
      .pipe(take(1), takeUntilDestroyed(this.destroy_ref))
      .subscribe({
        next: data => {
          this.holidaysActualUser.set(data);

          // Agregar festius (holidays)
          const holidays = data.holidays.map(h => dayjs(h.date).format('YYYY-MM-DD'));

          // Agregar vacances confirmades (employee_calendar_registers amb slug 'vacations')
          const vacations = data.employee_calendar_registers
            .filter(ecr => ecr.all_day && ecr.working_time_category?.slug === 'vacations')
            .flatMap(ecr => {
              const dates: string[] = [];
              const start = dayjs(ecr.from_date);
              const end = dayjs(ecr.to_date);
              let current = start;
              while (current.isSameOrBefore(end, 'day')) {
                dates.push(current.format('YYYY-MM-DD'));
                current = current.add(1, 'day');
              }
              return dates;
            });

          // Combinar festius i vacances
          this.holidays = [...new Set([...holidays, ...vacations])];
        },
      });
  }

  private patchFormFromAbsenceRequest(ar: AbsenceRequestWithDocument): void {
    const category = this.absencesWorkingTimeCategories().find(c => c.id === ar.working_time_category_id);
    if (category) {
      this.onWorkingTimeCategoryChange({ value: ar.working_time_category_id } as MatSelectChange);
    }
    this.form.patchValue({
      working_time_category_id: ar.working_time_category_id,
      justification: ar.justification ?? '',
    });

    const start_date = ar.start_date ? dayjs(ar.start_date) : null;
    const end_date = ar.end_date ? dayjs(ar.end_date) : null;
    if (this.isAllDay()) {
      this.form.patchValue({
        start_date: start_date?.format('YYYY-MM-DD') ?? null,
        end_date: end_date?.format('YYYY-MM-DD') ?? null,
      });
    } else {
      this.form.patchValue({
        start_date: start_date?.format('YYYY-MM-DD') ?? null,
        end_date: end_date?.format('YYYY-MM-DD') ?? null,
        start_time: start_date?.format('HH:mm') ?? '',
        end_time: end_date?.format('HH:mm') ?? '',
      });
    }

    this.applyStartedAbsenceModificationLocks();
  }

  private applyStartedAbsenceModificationLocks(): void {
    if (!this.isStartDateLocked()) {
      return;
    }

    this.form.get('start_date')?.disable({ emitEvent: false });
    this.form.get('start_time')?.disable({ emitEvent: false });

    if (this.is_consecutive_category()) {
      this.dateAdjustmentMessage.set(
        'La ausencia ya ha comenzado y esta categoría fija los días consecutivos: no puedes cambiar las fechas.'
      );
      return;
    }

    this.dateAdjustmentMessage.set(
      'La ausencia ya ha comenzado: no puedes cambiar la fecha de inicio. Sí puedes modificar la fecha de fin.'
    );
  }

  private getNormalizedFormValue(): Record<string, unknown> {
    const value = this.form.getRawValue();
    return {
      working_time_category_id: value.working_time_category_id,
      start_date: value.start_date ? dayjs(value.start_date).format('YYYY-MM-DD') : null,
      end_date: value.end_date ? dayjs(value.end_date).format('YYYY-MM-DD') : null,
      start_time: value.start_time ?? '',
      end_time: value.end_time ?? '',
      justification: value.justification ?? '',
    };
  }

  private setInitialFormValueSnapshot(): void {
    this.initialFormValueSnapshot = JSON.stringify(this.getNormalizedFormValue());
  }

  private hasFormChanged(): boolean {
    if (!this.isModificationMode()) {
      return this.form.dirty;
    }
    if (this.initialFormValueSnapshot == null) {
      return false;
    }
    return JSON.stringify(this.getNormalizedFormValue()) !== this.initialFormValueSnapshot;
  }
}
